import { useModal } from '@jutro/components';
import { DropdownMenuLink } from '@jutro/router';
import { PhoneNumberField, DropdownMenuButton } from '@jutro/legacy/components';
import { useTranslator } from '@jutro/locale';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { ViewModelForm,ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React,{ useContext, useState, useEffect, useCallback } from 'react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { TableRowUtil } from 'wnice-portals-util-react';
import { DRIVER_PATH } from '../../config/vehicles.static';
import { getFullAddressDisplayName } from '../../utils/AddressUtil';
import metadata from './Contact.metadata.json5';
import ContactDetails from './ContactDetails';
import messages from '../PolicyVehicle/PolicyVehicle.messages';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';


const CONTACT_DTO = 'wni.edge.capabilities.claim.contact.dto.WniContactDTO';
const xCenter = 'cc';

function Contact(props) {
    const modalApi = useModal();
    const {
        id: pageId,
        model: currentRow,
        basePath,
        dataPath,
        contactRole,
        tableData = [],
        title,
        buttonText,
        onValidate = _.noop,
        writeValue = _.noop,
        tableConfig = {},
        selection,
        onSelectionChange,
        showPerson,
        incidentType
    } = props;

    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        loadingMask: { setLoadingMask },
    } = useDependencies('loadingMask');
    const {selectionType} = tableConfig;
    const [contactVM, updateContactVM] = useState(null);
    const allDataVM = _.get(currentRow, dataPath);
    let allData = _.get(allDataVM, 'value', []);
    // CEI-6485 Third party vehicle don't display policy contact
    if(incidentType === 'third_party' && basePath === DRIVER_PATH){
        allData = allData.filter((person)=> !person.policySystemId && !person.roles?.includes('Covered Party'))
    }

    const highlightRowFn = (activeRow) => {
        const activePublicID = activeRow ? _.get(activeRow.value, 'rowIdPath') : null;
        TableRowUtil.setTablePublicIDSelected(activePublicID, pageId);
    };

    useEffect(() => {
        highlightRowFn(contactVM);
    }, [contactVM]);

    const handleSelectionChange = useCallback((ids) => {
        onSelectionChange(ids, basePath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const addContact = () => {
        const initData = {
            rowIdPath: `cc:${TableRowUtil.getUuid()}`,
            primaryAddress: { 
                country: 'US'
            },
            subtype: "Person",
            isSave: true
        }
        const initVM = viewModelService.create(initData, xCenter, CONTACT_DTO);
        updateContactVM(initVM);
    };
    const editContact = (item) => {
        const initData = {
            ...item,
            primaryAddress: item.primaryAddress || { country: 'US' },
            isSave: true
        }
        const initVM = viewModelService.create(initData, xCenter, CONTACT_DTO);
        updateContactVM(initVM);
    };

    const removeContact = (item) => {
        modalApi.showConfirm({
            title: messages.removeIncidentTitle,
            message: messages.removeIncident,
            status: 'warning',
            icon: 'gw-error-outline',
        }).then(async (results) => {
            if (results === 'cancel' || results === 'close') {
                return false;
            }
            onSelectionChange([], basePath)
            const newData = tableData.filter((contact) => contact.rowIdPath !== item.rowIdPath)
            writeValue(newData, dataPath);
            return true;
        }, _.noop);
    };

    const renderPhoneCell = (item) => {
        const phoneNumber = item.workNumber || item.homeNumber || item.cellNumber;
        return <PhoneNumberField value={phoneNumber} readOnly />
    };

    const renderActionCell = (item, index) => {
        if (item.policySystemId) {
            return null;
        }
        const dom = (
            <DropdownMenuButton
                icon="gw-expand-more"
                id="dropdownMenuButton"
                className="dropDownMenuIconbtn"
                menuClassName="dropDownMenuList"
                alignRight
            >
                <DropdownMenuLink icon="gw-edit" onClick={() => editContact(item)}>Edit</DropdownMenuLink>
                <DropdownMenuLink icon="gw-delete" onClick={() => removeContact(item)}>Remove</DropdownMenuLink>
            </DropdownMenuButton>
        );
        return TableRowUtil.renderCell(item.rowIdPath, dom);
    };


    const onValueChange = (value, path) => {
        if(contactVM) {
            const newVM = viewModelService.clone(contactVM);
            const isCurrencyField = _.isObject(value) && _.get(value, 'currency');
            if (isCurrencyField && (_.isNil(_.get(value, 'amount')) || _.get(value, 'amount') === '')){
                _.set(newVM.value, path, undefined);
            } else {
                _.set(newVM.value, path, value);
            }
            updateContactVM(newVM);
        }
    };

    const generateVM = (vm) => {
        const newVM = viewModelService.clone(vm);
        const contactAddress = _.get(newVM.value, 'primaryAddress', {});
        const newContactAddress = _.omit(contactAddress, 'country'); // country not be checked
        const isAddressEmpty = _.every(newContactAddress, _.isEmpty);
        if(isAddressEmpty) {
            _.set(newVM.value, 'primaryAddress', null);
        }
        return newVM
    };

    const getPersonAvailablePersons = () => {
        const contacts = _.filter(allData, contact => !contact.policySystemId)
        const availablePersons = _.map(contacts, (person) => {
            return {
                code: person.rowIdPath,
                name: person.displayName || `${_.get(person, 'firstName', '')} ${_.get(person, 'lastName', '')}`
            }
        })
        return availablePersons.concat({code: 'newPerson', name: 'New Person'})
    }

    const handlePersonChange = (contactId) => {
        const currentContact = allData.find((item) => item.rowIdPath === contactId);

        const initData = {
            ...currentContact,
            rowIdPath: currentContact?.rowIdPath || `cc:${TableRowUtil.getUuid()}`,
            subtype: currentContact?.subtype || "Person",
            primaryAddress: currentContact?.primaryAddress || { country: 'US' },
            isSave: true,

        }
        const initVM = viewModelService.create(initData, xCenter, CONTACT_DTO);
        updateContactVM(initVM);
    }

    const renderAddressCell = (item) => {
        const primaryAddress = _.get(item, 'primaryAddress');
        if (_.isEmpty(primaryAddress)) {
            return '-';
        }
        return getFullAddressDisplayName(_.get(item, 'primaryAddress'));
    }

    const save = () => {
        const currentVM = generateVM(contactVM);
        const rowIdPath = _.get(currentVM.value, 'rowIdPath');
        const findCurrentContact = allData.find((item) => item.rowIdPath === rowIdPath)
        if(findCurrentContact) { // edit existing contact
            const currentIndex = allData.findIndex((item) => item.rowIdPath === rowIdPath);
            const newModel = viewModelService.clone(currentRow);
            _.set(newModel.value, `${dataPath}[${currentIndex}]`, currentVM.value);
            const newDataVM = _.get(newModel, dataPath)
            writeValue(newDataVM.value, dataPath);
            updateContactVM(null);
            return false;
        }

        // add new contact
        allDataVM.pushElement(currentVM);
        writeValue(allDataVM.value, dataPath);
        updateContactVM(null);
    };

    const cancel = () => {
        updateContactVM(null);
    };

    const overrides = {
        contactTitleText:{
            content: translator(title)
        },
        contactTable: {
            id: pageId,
            data: allData,
            selectedRows: selection,
            onSelectionChange: handleSelectionChange,
            ...tableConfig
        },
        addButton: {
            content: buttonText,
            disabled: contactVM
        },
        contactDetailsContainer: {
            visible: !!contactVM
        },
        contactDetails: {
            contactVM: contactVM,
            writeValue: onValueChange,
            save,
            cancel,
            showPerson,
            availablePersons: getPersonAvailablePersons(),
            handlePersonChange,
            incidentType
        }
    };
    const resolvers = {
        resolveComponentMap: {
            contactdetails: ContactDetails
        },
        resolveCallbackMap: {
            renderPhoneCell,
            renderActionCell,
            addContact,
            renderAddressCell
        },
    };

    const readValue = (id, path) => {
        return readViewModelValue(
            metadata.componentContent,
            tableData,
            id,
            path,
            overrides
        );
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={tableData}
            overrideProps={overrides}
            onValueChange={onValueChange}
            resolveValue={readValue}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

export default Contact;
